import { apiFetch, ApiError } from "./client";
import type { ViewConfig } from "@/lib/views";

export type { ViewConfig };

/** Ошибка optimistic-locking конфликта при сохранении вида.
 *  Бросается из updateView когда сервер ответил 409 с code: "VIEW_CONFLICT".
 *  Вызывающий код должен поймать это и предложить юзеру перезагрузить вид. */
export class ViewConflictError extends Error {
  constructor() {
    super("View was modified elsewhere");
    this.name = "ViewConflictError";
  }
}

export async function fetchView(id: string): Promise<ViewConfig> {
  return apiFetch<ViewConfig>(`/api/views?id=${encodeURIComponent(id)}`);
}

export async function fetchViews(): Promise<ViewConfig[]> {
  const data = await apiFetch<{ views: ViewConfig[] }>("/api/views");
  return data.views;
}

export async function createView(
  name: string,
  columns: string[],
  rest?: Partial<Omit<ViewConfig, "id" | "name" | "columns" | "accountId" | "createdByUserId" | "createdAt" | "updatedAt">>,
  /** Если задан — новый вид получит `createdAt` чуть позже source.createdAt,
   *  поэтому в списке (order by created_at) встанет сразу под source. */
  insertAfterId?: string,
): Promise<ViewConfig> {
  return apiFetch<ViewConfig>("/api/views", {
    method: "POST",
    body: JSON.stringify({ action: "create", name, columns, ...rest, ...(insertAfterId ? { insertAfterId } : {}) }),
  });
}

export async function updateView(
  id: string,
  patch: Partial<ViewConfig>,
  /** Optimistic-locking версия: значение `updatedAt` которое клиент видел в
   *  последний раз. Если на сервере оно отличается, апдейт отклоняется с 409. */
  expectedUpdatedAt?: string,
): Promise<ViewConfig> {
  try {
    return await apiFetch<ViewConfig>("/api/views", {
      method: "POST",
      body: JSON.stringify({ action: "update", id, ...patch, ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}) }),
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.body && typeof err.body === "object" && "code" in err.body && (err.body as { code: string }).code === "VIEW_CONFLICT") {
      throw new ViewConflictError();
    }
    throw err;
  }
}

export async function deleteView(id: string): Promise<void> {
  await apiFetch("/api/views", {
    method: "POST",
    body: JSON.stringify({ action: "delete", id }),
  });
}

export async function shareView(id: string): Promise<string> {
  const data = await apiFetch<{ shareToken: string }>("/api/views", {
    method: "POST",
    body: JSON.stringify({ action: "share", id }),
  });
  return data.shareToken;
}

export async function unshareView(id: string): Promise<void> {
  await apiFetch("/api/views", {
    method: "POST",
    body: JSON.stringify({ action: "unshare", id }),
  });
}
